import { normalizeCouponCode } from '../commerce/community-membership-tiers';

export type CourseCouponRef = {
  code: string;
  courseId: string;
  percentOff?: number | null;
  amountOffCents?: number | null;
  maxRedemptions?: number | null;
  redemptionCount?: number;
  expiresAt?: Date | string | null;
  enabled?: boolean;
};

export { normalizeCouponCode };

export function validateCourseCoupon(params: {
  coupon: CourseCouponRef;
  courseId: string;
  now?: Date;
}): { ok: true } | { ok: false; message: string } {
  const now = params.now ?? new Date();
  if (params.coupon.courseId !== params.courseId) {
    return { ok: false, message: 'Coupon does not apply to this course' };
  }
  if (params.coupon.enabled === false) {
    return { ok: false, message: 'Coupon is not active' };
  }
  if (params.coupon.expiresAt) {
    const expiresAt =
      params.coupon.expiresAt instanceof Date
        ? params.coupon.expiresAt
        : new Date(params.coupon.expiresAt);
    if (expiresAt.getTime() <= now.getTime()) {
      return { ok: false, message: 'Coupon has expired' };
    }
  }
  if (
    params.coupon.maxRedemptions != null &&
    (params.coupon.redemptionCount ?? 0) >= params.coupon.maxRedemptions
  ) {
    return { ok: false, message: 'Coupon redemption limit reached' };
  }
  if (params.coupon.percentOff == null && params.coupon.amountOffCents == null) {
    return { ok: false, message: 'Coupon is misconfigured' };
  }
  if (
    params.coupon.percentOff != null &&
    (params.coupon.percentOff < 1 || params.coupon.percentOff > 100)
  ) {
    return { ok: false, message: 'Coupon is misconfigured' };
  }
  if (params.coupon.amountOffCents != null && params.coupon.amountOffCents < 1) {
    return { ok: false, message: 'Coupon is misconfigured' };
  }
  return { ok: true };
}

export function computeCourseDiscountCents(
  amountCents: number,
  coupon: Pick<CourseCouponRef, 'percentOff' | 'amountOffCents'>,
): number {
  if (amountCents <= 0) return 0;
  if (coupon.percentOff != null) {
    return Math.min(amountCents, Math.round((amountCents * coupon.percentOff) / 100));
  }
  if (coupon.amountOffCents != null) {
    return Math.min(amountCents, coupon.amountOffCents);
  }
  return 0;
}

export function computeCourseCheckoutAmountCents(
  amountCents: number,
  coupon: Pick<CourseCouponRef, 'percentOff' | 'amountOffCents'> | null,
): number {
  if (!coupon) return amountCents;
  const discount = computeCourseDiscountCents(amountCents, coupon);
  return Math.max(0, amountCents - discount);
}
